import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Eye, Save, ShieldAlert, KeyRound, Plus, Trash2, Power } from "lucide-react";
import SignupCodesManager from "@/components/admin/SignupCodesManager";

export default function AdminSettings() {
  const [html, setHtml] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [allowed, setAllowed] = useState(null); // null=loading, true/false

  useEffect(() => {
    api.get("/admin/terms").then((r) => {
      setHtml(r.data.terms_html || "");
      setUpdatedAt(r.data.updated_at || "");
      setAllowed(!!r.data.is_platform_owner);
    }).catch(() => { setAllowed(false); toast.error("No pudimos cargar la configuración"); });
  }, []);

  async function save() {
    if (!allowed) return;
    if (html.trim().length < 10) { toast.error("El contenido es muy corto"); return; }
    setLoading(true);
    try {
      const { data } = await api.put("/admin/terms", { terms_html: html });
      setUpdatedAt(data.updated_at);
      toast.success("Términos actualizados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setLoading(false); }
  }

  if (allowed === false) {
    return (
      <div>
        <div className="mb-8">
          <p className="overline">Plataforma</p>
          <h1 className="font-display text-4xl font-light tracking-tight mt-3">Configuración</h1>
        </div>
        <Card className="border-amber-200 bg-amber-50" data-testid="settings-forbidden">
          <CardContent className="p-8 flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-amber-100 grid place-items-center shrink-0">
              <ShieldAlert className="h-6 w-6 text-amber-700" />
            </div>
            <div>
              <p className="font-display text-xl">Acceso restringido</p>
              <p className="text-slate-700 text-sm mt-2 max-w-prose">
                La sección <strong>Configuración</strong> solo puede ser modificada por la administración de la plataforma Book-It.
                Si necesitas ajustar los términos y condiciones que ven tus clientes en tu propio negocio (políticas de cancelación, etc.),
                contáctanos.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Plataforma</p>
          <h1 className="font-display text-4xl font-light tracking-tight mt-3">Configuración</h1>
        </div>
        <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="settings-owner-badge">
          <ShieldAlert className="h-3 w-3" /> Modo administrador de plataforma
        </div>
      </div>

      <Card className="border-slate-200" data-testid="settings-terms-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-[hsl(var(--accent))]" />
              <p className="overline">Términos y condiciones</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setPreview((p) => !p)} data-testid="settings-terms-preview">
              <Eye className="h-3 w-3 mr-1" /> {preview ? "Editar" : "Previsualizar"}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mb-4">Edita el contenido que verán los nuevos negocios al registrarse en la plataforma. Soporta HTML básico (h2, p, ul, li, strong, em, a).</p>

          {preview ? (
            <div className="prose prose-sm max-w-none rounded-md border border-slate-200 bg-white p-6 min-h-[300px]" data-testid="settings-terms-preview-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
          ) : (
            <Textarea
              rows={18}
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              className="font-mono text-sm"
              data-testid="settings-terms-textarea"
            />
          )}

          <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-slate-500">{updatedAt ? `Última actualización: ${new Date(updatedAt).toLocaleString("es-MX")}` : "Sin cambios todavía"}</p>
            <Button onClick={save} disabled={loading} data-testid="settings-terms-save">
              <Save className="h-3 w-3 mr-1" /> {loading ? "Guardando…" : "Guardar términos"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mt-6">
        <SignupCodesManager />
      </div>
    </div>
  );
}
